// Shared feature/service-request triage contract (typed help/intake framework,
// commit 4).
//
// Third sibling of feedbackIntakes.js (the intake AGGREGATE vocabulary) and
// supportTickets.js (the GSC support vertical). This module owns the
// `feature_service_request` vertical:
//
//   - the ordered probe topics and the DETERMINISTIC question for each one, which
//     is the always-works conversation path when no model is reachable;
//   - the preliminary-RCA field contract and its pure validator, which is the
//     only thing standing between raw model output and persisted state.
//
// Consumed by:
//   - apps/api/src/services/requestTriage.service.js       (engine + validation)
//   - apps/api/src/services/feedbackIntakes.service.js     (readiness + patch)
//   - apps/web/src/features/feedbackIntake/RequestIntakePage.jsx (progress, editors)
//
// Pure data plus pure functions: no React, no Node-only imports, no API access.
//
// Two rules are structural, not stylistic:
//
//   1. THE SERVER IS AUTHORITATIVE. The web imports the same validator so the
//      form can refuse early, but nothing is trusted because a browser accepted
//      it.
//   2. MODEL OUTPUT IS UNTRUSTED INPUT. The validator rejects unknown keys rather
//      than dropping them, bounds every string and array, and DISCARDS any
//      model-supplied `confidence` — that value is derived from validated content
//      by derivePreliminaryRcaConfidence() and capped by real provenance in the
//      service. A model must never be able to tell a reporter how sure to be.

// -------------------------------------------------------------------------
// Probe topics
// -------------------------------------------------------------------------

// Ordered on purpose: this is the sequence a reporter is walked through when no
// model is available, and it moves from "what do you want" through "what is
// happening now" to "what constrains the fix". A topic's POSITION is part of the
// contract; inserting one in the middle changes the deterministic conversation.
export const REQUEST_PROBE_TOPICS = Object.freeze([
  'desiredOutcome',
  'currentProcess',
  'coreProblem',
  'affectedUsers',
  'frequency',
  'scope',
  'operationalImpact',
  'expectedBehavior',
  'actualBehavior',
  'currentWorkaround',
  'dependencies',
  'urgency',
  'desiredTiming',
  'constraints',
  'supportingEvidence',
]);

// Short human labels for the coverage/progress affordance. Sentence case, no
// jargon — these are read by a reporter, not a developer.
export const REQUEST_PROBE_TOPIC_LABELS = Object.freeze({
  desiredOutcome: 'What you want to be able to do',
  currentProcess: 'How you do it today',
  coreProblem: 'The core problem',
  affectedUsers: 'Who else is affected',
  frequency: 'How often it comes up',
  scope: 'How much work it covers',
  operationalImpact: 'The operational impact',
  expectedBehavior: 'What you expected',
  actualBehavior: 'What happens instead',
  currentWorkaround: 'Your workaround today',
  dependencies: 'Other teams or systems involved',
  urgency: 'How urgent it is',
  desiredTiming: 'When you need it',
  constraints: 'Constraints PRIME must respect',
  supportingEvidence: 'Evidence you can point to',
});

// USER-VISIBLE COPY. One deterministic, well-written question per topic, in the
// voice of a colleague who needs a usable answer: plain words, one ask, no
// stacked sub-questions, and never a claim that an assistant composed it.
export const REQUEST_FALLBACK_QUESTIONS = Object.freeze({
  desiredOutcome: 'What do you want to be able to do that you cannot do in PRIME today?',
  currentProcess: 'How do you handle this today — which steps or tools do you use?',
  coreProblem: 'What is the core problem with the way it works now?',
  affectedUsers: 'Who else runs into this, and roughly how many people?',
  frequency: 'How often does this come up — daily, weekly, or only at certain times?',
  scope: 'Is this about one program, one portfolio, or everything you touch in PRIME?',
  operationalImpact: 'What does this cost you when it happens — time, rework, a missed deadline?',
  expectedBehavior: 'What did you expect PRIME to do?',
  actualBehavior: 'What does PRIME do instead?',
  currentWorkaround: 'What are you doing to work around it right now?',
  dependencies: 'Does this depend on another team, system, or data source?',
  urgency: 'What makes this urgent, or can it wait for a normal release?',
  desiredTiming: 'When do you need this in place, and what drives that date?',
  constraints:
    'Are there rules or constraints PRIME has to respect here — policy, classification, an approval step?',
  supportingEvidence: 'Is there a screen, report, or message you can point us to as an example?',
});

// A reporter should not have to answer fifteen questions before PRIME will draft
// anything. Three answered topics is the floor, and two of them are named
// because an analysis with no stated problem or no stated goal is not an
// analysis.
export const REQUEST_MINIMUM_PROBED_TOPICS = 3;
export const REQUEST_REQUIRED_PROBE_TOPICS = Object.freeze(['coreProblem', 'desiredOutcome']);

// How many times one topic may be asked before the deterministic tracker moves
// on. Without this a reporter who skips a question is asked it forever.
export const REQUEST_MAX_ASKS_PER_TOPIC = 2;

export const REQUEST_PROBE_SOURCES = Object.freeze(['ai', 'deterministic']);

export function isRequestProbeTopic(value) {
  return REQUEST_PROBE_TOPICS.includes(value);
}

export function isRequestProbeSource(value) {
  return REQUEST_PROBE_SOURCES.includes(value);
}

// Coverage from the transcript alone, so the server and the web agree without a
// second stored counter that could drift from the messages.
//
// A topic counts as COVERED when a `user` turn carries that topic tag — i.e. the
// reporter answered a question PRIME asked. Assistant turns only contribute ask
// counts. Empty/whitespace answers do not count.
export function evaluateProbeCoverage({ messages = [] } = {}) {
  const covered = [];
  const askCounts = {};
  for (const message of messages) {
    const topic = message?.topic ?? message?.payload?.topic ?? null;
    if (!isRequestProbeTopic(topic)) continue;
    if (message.actorType === 'assistant') {
      askCounts[topic] = (askCounts[topic] || 0) + 1;
      continue;
    }
    if (message.actorType !== 'user') continue;
    if (!String(message.content ?? '').trim()) continue;
    if (!covered.includes(topic)) covered.push(topic);
  }
  const remaining = REQUEST_PROBE_TOPICS.filter((topic) => !covered.includes(topic));
  const missingRequired = REQUEST_REQUIRED_PROBE_TOPICS.filter((topic) => !covered.includes(topic));
  return {
    covered,
    remaining,
    askCounts,
    minimum: REQUEST_MINIMUM_PROBED_TOPICS,
    missingRequired,
    // The gate the "Generate preliminary analysis" action reads.
    ready: covered.length >= REQUEST_MINIMUM_PROBED_TOPICS && missingRequired.length === 0,
  };
}

// The next topic to ask about, in contract order, skipping topics that have
// already been asked REQUEST_MAX_ASKS_PER_TOPIC times without an answer. Returns
// null only when every topic is either covered or exhausted.
export function nextProbeTopic({ messages = [] } = {}) {
  const { remaining, askCounts } = evaluateProbeCoverage({ messages });
  const unexhausted = remaining.find(
    (topic) => (askCounts[topic] || 0) < REQUEST_MAX_ASKS_PER_TOPIC,
  );
  return unexhausted || null;
}

// -------------------------------------------------------------------------
// Preliminary RCA contract
// -------------------------------------------------------------------------

// Stamped onto every persisted RCA. A reader that does not recognize the version
// must refuse to interpret the object rather than guess at its keys.
export const PRELIMINARY_RCA_SCHEMA_VERSION = 'preliminary-rca/1.0';

// The heading is part of the contract, not a presentation detail: this analysis
// is a HYPOTHESIS, and every surface that renders it must say so in the title.
export const PRELIMINARY_RCA_TITLE = 'Preliminary problem analysis — user confirmation required';

export const RCA_CONTRIBUTING_FACTOR_CATEGORIES = Object.freeze([
  'process',
  'people_roles',
  'policy',
  'data',
  'application_behavior',
  'integration',
  'infrastructure',
  'training_usability',
]);

export const RCA_CONTRIBUTING_FACTOR_CATEGORY_LABELS = Object.freeze({
  process: 'Process',
  people_roles: 'People and roles',
  policy: 'Policy',
  data: 'Data',
  application_behavior: 'Application behavior',
  integration: 'Integration',
  infrastructure: 'Infrastructure',
  training_usability: 'Training and usability',
});

// DISPLAY ONLY, and never set from model output — see derivePreliminaryRcaConfidence.
export const RCA_CONFIDENCE_LEVELS = Object.freeze(['low', 'medium', 'high']);

// Advisory routing hint shown to the reporter and the reviewer. It does NOT set
// the governed record's category: that stays server-owned in
// FEEDBACK_INTAKE_TYPE_DEFAULTS, so a model cannot re-route work.
export const RCA_SUGGESTED_CATEGORIES = Object.freeze([
  'enhancement',
  'new_capability',
  'process_change',
  'integration',
  'data_quality',
  'training',
  'other',
]);

export const RCA_SUGGESTED_CATEGORY_LABELS = Object.freeze({
  enhancement: 'Enhancement to something PRIME already does',
  new_capability: 'A new capability',
  process_change: 'A process change',
  integration: 'An integration with another system',
  data_quality: 'A data-quality problem',
  training: 'Training or guidance',
  other: 'Something else',
});

// Bounds. Measured in UTF-16 code units via String.length — the same unit
// NVARCHAR counts, and identical in the current Node and browser runtimes.
// Exactly the limit is accepted; one more is not.
export const PRELIMINARY_RCA_TEXT_FIELD_LIMITS = Object.freeze({
  problemStatement: 2000,
  desiredOutcome: 2000,
  currentWorkaround: 1000,
  impact: 1000,
  recommendedNextAction: 600,
});

export const PRELIMINARY_RCA_LIST_FIELD_LIMITS = Object.freeze({
  observedSymptoms: Object.freeze({ maxItems: 12, maxLength: 400 }),
  knownFacts: Object.freeze({ maxItems: 12, maxLength: 400 }),
  assumptions: Object.freeze({ maxItems: 12, maxLength: 400 }),
  missingInformation: Object.freeze({ maxItems: 12, maxLength: 400 }),
  affectedCapabilities: Object.freeze({ maxItems: 12, maxLength: 200 }),
});

export const PRELIMINARY_RCA_MAX_CONTRIBUTING_FACTORS = 12;
export const PRELIMINARY_RCA_CONTRIBUTING_FACTOR_MAX_LENGTH = 400;
export const PRELIMINARY_RCA_MAX_HYPOTHESES = 6;
export const PRELIMINARY_RCA_HYPOTHESIS_MAX_LENGTH = 400;
export const PRELIMINARY_RCA_MAX_EVIDENCE_ITEMS = 8;
export const PRELIMINARY_RCA_EVIDENCE_MAX_LENGTH = 300;
export const PRELIMINARY_RCA_MAX_TURN_REFS = 8;
export const PRELIMINARY_RCA_TURN_REF_MAX_LENGTH = 128;
export const PRELIMINARY_RCA_HYPOTHESIS_ID_MAX_LENGTH = 64;

// The fields a MODEL may propose and a reporter may edit. Anything outside this
// list is rejected by the validator — including every server-owned field below,
// so a client PATCH that tries to stamp its own approval gets a precise refusal
// instead of a silent drop.
export const PRELIMINARY_RCA_CONTENT_FIELDS = Object.freeze([
  'problemStatement',
  'desiredOutcome',
  'observedSymptoms',
  'knownFacts',
  'assumptions',
  'missingInformation',
  'possibleContributingFactors',
  'hypotheses',
  'currentWorkaround',
  'affectedCapabilities',
  'impact',
  'suggestedCategory',
  'recommendedNextAction',
  'confidence',
]);

// Server-owned. Never accepted from a client or a model; written only by the
// intake service inside the mutation transaction.
export const PRELIMINARY_RCA_SERVER_FIELDS = Object.freeze([
  'schemaVersion',
  'source',
  'generatedAt',
  'provenance',
  'userEdited',
  'approvedAt',
  'approvedRevision',
]);

// How the persisted RCA came to exist. `ai_generated` requires a real non-mock
// provider AND validated JSON; `deterministic_skeleton` is the prefilled template
// a reporter fills in themselves, which is why submission never depends on AI.
export const PRELIMINARY_RCA_SOURCES = Object.freeze(['ai_generated', 'deterministic_skeleton']);

export function isRcaContributingFactorCategory(value) {
  return RCA_CONTRIBUTING_FACTOR_CATEGORIES.includes(value);
}

export function isRcaConfidenceLevel(value) {
  return RCA_CONFIDENCE_LEVELS.includes(value);
}

export function isRcaSuggestedCategory(value) {
  return RCA_SUGGESTED_CATEGORIES.includes(value);
}

export function isPreliminaryRcaSource(value) {
  return PRELIMINARY_RCA_SOURCES.includes(value);
}

// The canonical empty content object. Every field present, so a consumer never
// has to distinguish "absent" from "empty" — and the deterministic skeleton is
// this object with a few strings filled in.
export function emptyPreliminaryRcaContent() {
  return {
    problemStatement: '',
    desiredOutcome: '',
    observedSymptoms: [],
    knownFacts: [],
    assumptions: [],
    missingInformation: [],
    possibleContributingFactors: [],
    hypotheses: [],
    currentWorkaround: '',
    affectedCapabilities: [],
    impact: '',
    suggestedCategory: null,
    recommendedNextAction: '',
    confidence: 'low',
  };
}

function textLength(value) {
  return String(value ?? '').length;
}

function pushError(errors, field, reason, message) {
  errors.push({ field, reason, message });
}

// Bounded string list: array-of-strings, item count and item length checked,
// blank entries dropped (an empty bullet is noise, not information).
function normalizeStringList(value, { field, maxItems, maxLength, errors }) {
  if (value == null) return [];
  if (!Array.isArray(value)) {
    pushError(errors, field, 'type', `${field} must be an array of text entries.`);
    return [];
  }
  if (value.length > maxItems) {
    pushError(
      errors,
      field,
      'bound',
      `${field} may contain at most ${maxItems} entries (received ${value.length}).`,
    );
    return [];
  }
  const normalized = [];
  value.forEach((entry, index) => {
    if (typeof entry !== 'string') {
      pushError(errors, `${field}[${index}]`, 'type', `${field}[${index}] must be text.`);
      return;
    }
    const trimmed = entry.trim();
    if (!trimmed) return;
    if (textLength(trimmed) > maxLength) {
      pushError(
        errors,
        `${field}[${index}]`,
        'length',
        `${field}[${index}] must be ${maxLength} characters or fewer (received ${textLength(trimmed)}).`,
      );
      return;
    }
    normalized.push(trimmed);
  });
  return normalized;
}

function normalizeText(value, { field, maxLength, errors }) {
  if (value == null) return '';
  if (typeof value !== 'string') {
    pushError(errors, field, 'type', `${field} must be text.`);
    return '';
  }
  const trimmed = value.trim();
  if (textLength(trimmed) > maxLength) {
    pushError(
      errors,
      field,
      'length',
      `${field} must be ${maxLength} characters or fewer (received ${textLength(trimmed)}).`,
    );
    return '';
  }
  return trimmed;
}

function normalizeContributingFactors(value, errors) {
  const field = 'possibleContributingFactors';
  if (value == null) return [];
  if (!Array.isArray(value)) {
    pushError(errors, field, 'type', `${field} must be an array.`);
    return [];
  }
  if (value.length > PRELIMINARY_RCA_MAX_CONTRIBUTING_FACTORS) {
    pushError(
      errors,
      field,
      'bound',
      `${field} may contain at most ${PRELIMINARY_RCA_MAX_CONTRIBUTING_FACTORS} entries (received ${value.length}).`,
    );
    return [];
  }
  const normalized = [];
  value.forEach((entry, index) => {
    const at = `${field}[${index}]`;
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      pushError(errors, at, 'type', `${at} must be an object.`);
      return;
    }
    const unknown = Object.keys(entry).filter((key) => !['category', 'description'].includes(key));
    if (unknown.length) {
      pushError(errors, at, 'unknown_key', `${at} does not support: ${unknown.join(', ')}.`);
      return;
    }
    if (!isRcaContributingFactorCategory(entry.category)) {
      pushError(
        errors,
        `${at}.category`,
        'enum',
        `${at}.category must be one of: ${RCA_CONTRIBUTING_FACTOR_CATEGORIES.join(', ')}.`,
      );
      return;
    }
    const description = normalizeText(entry.description, {
      field: `${at}.description`,
      maxLength: PRELIMINARY_RCA_CONTRIBUTING_FACTOR_MAX_LENGTH,
      errors,
    });
    if (!description) {
      pushError(errors, `${at}.description`, 'required', `${at}.description is required.`);
      return;
    }
    normalized.push({ category: entry.category, description });
  });
  return normalized;
}

// Hypothesis ids are REASSIGNED deterministically (H1, H2, …) after validation.
// A model-supplied id is validated for shape so a malformed one is caught rather
// than silently replaced, but the stored id is always the server's — the UI uses
// it as a remove key, and duplicate or exotic ids would break that.
function normalizeHypotheses(value, errors) {
  const field = 'hypotheses';
  if (value == null) return [];
  if (!Array.isArray(value)) {
    pushError(errors, field, 'type', `${field} must be an array.`);
    return [];
  }
  if (value.length > PRELIMINARY_RCA_MAX_HYPOTHESES) {
    pushError(
      errors,
      field,
      'bound',
      `${field} may contain at most ${PRELIMINARY_RCA_MAX_HYPOTHESES} entries (received ${value.length}).`,
    );
    return [];
  }
  const allowed = [
    'id',
    'statement',
    'supportingEvidence',
    'contradictingEvidence',
    'sourceTurnRefs',
  ];
  const normalized = [];
  value.forEach((entry, index) => {
    const at = `${field}[${index}]`;
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      pushError(errors, at, 'type', `${at} must be an object.`);
      return;
    }
    const unknown = Object.keys(entry).filter((key) => !allowed.includes(key));
    if (unknown.length) {
      pushError(errors, at, 'unknown_key', `${at} does not support: ${unknown.join(', ')}.`);
      return;
    }
    if (entry.id != null) {
      if (typeof entry.id !== 'string') {
        pushError(errors, `${at}.id`, 'type', `${at}.id must be text.`);
        return;
      }
      if (textLength(entry.id) > PRELIMINARY_RCA_HYPOTHESIS_ID_MAX_LENGTH) {
        pushError(
          errors,
          `${at}.id`,
          'length',
          `${at}.id must be ${PRELIMINARY_RCA_HYPOTHESIS_ID_MAX_LENGTH} characters or fewer.`,
        );
        return;
      }
    }
    const statement = normalizeText(entry.statement, {
      field: `${at}.statement`,
      maxLength: PRELIMINARY_RCA_HYPOTHESIS_MAX_LENGTH,
      errors,
    });
    if (!statement) {
      pushError(errors, `${at}.statement`, 'required', `${at}.statement is required.`);
      return;
    }
    normalized.push({
      id: `H${normalized.length + 1}`,
      statement,
      supportingEvidence: normalizeStringList(entry.supportingEvidence, {
        field: `${at}.supportingEvidence`,
        maxItems: PRELIMINARY_RCA_MAX_EVIDENCE_ITEMS,
        maxLength: PRELIMINARY_RCA_EVIDENCE_MAX_LENGTH,
        errors,
      }),
      contradictingEvidence: normalizeStringList(entry.contradictingEvidence, {
        field: `${at}.contradictingEvidence`,
        maxItems: PRELIMINARY_RCA_MAX_EVIDENCE_ITEMS,
        maxLength: PRELIMINARY_RCA_EVIDENCE_MAX_LENGTH,
        errors,
      }),
      sourceTurnRefs: normalizeStringList(entry.sourceTurnRefs, {
        field: `${at}.sourceTurnRefs`,
        maxItems: PRELIMINARY_RCA_MAX_TURN_REFS,
        maxLength: PRELIMINARY_RCA_TURN_REF_MAX_LENGTH,
        errors,
      }),
    });
  });
  return normalized;
}

/**
 * Deterministic display confidence.
 *
 * Derived from WHAT THE ANALYSIS CONTAINS, never from a model's self-assessment:
 * a hypothesis with evidence and no open questions is worth more than a
 * confident-sounding paragraph. The service lowers this further when provenance
 * shows a seeded, illustrative, or fallback run.
 */
export function derivePreliminaryRcaConfidence(content = {}) {
  const hasProblem = Boolean(String(content.problemStatement || '').trim());
  const hasOutcome = Boolean(String(content.desiredOutcome || '').trim());
  const hypotheses = Array.isArray(content.hypotheses) ? content.hypotheses : [];
  const evidenced = hypotheses.filter(
    (hypothesis) =>
      Array.isArray(hypothesis?.supportingEvidence) && hypothesis.supportingEvidence.length > 0,
  ).length;
  const facts = Array.isArray(content.knownFacts) ? content.knownFacts.length : 0;
  const openQuestions = Array.isArray(content.missingInformation)
    ? content.missingInformation.length
    : 0;

  if (!hasProblem || !hasOutcome) return 'low';
  if (evidenced >= 2 && facts >= 3 && openQuestions === 0) return 'high';
  if (evidenced >= 1 && facts >= 1) return 'medium';
  return 'low';
}

/**
 * The provenance INVARIANT, not a one-time transformation.
 *
 * A degraded run — seeded corpus, illustrative content, a silent fallback to the
 * mock, or a partial execution (retrieval failed / response truncated) — can never
 * display better than `low` confidence, no matter how complete its content looks.
 *
 * This must be applied at EVERY point where analysis content is written, not only at
 * generation: the audit found that a trivial reporter edit re-derived confidence from
 * the content alone and could lift a seeded analysis from `low` to `high` while its
 * provenance still said "illustrative". Confidence is a claim about how much to trust
 * the analysis, and the grounding is half of that claim.
 *
 * Pure and idempotent, so it is safe to apply on generation, on every patch, on
 * approval, and again at materialization.
 */
export function isDegradedRcaProvenance(provenance) {
  return Boolean(
    provenance?.seeded ||
    provenance?.illustrative ||
    provenance?.fallbackOccurred ||
    provenance?.fallback?.occurred ||
    provenance?.partialExecution?.occurred,
  );
}

export function enforceRcaProvenancePolicy(content, provenance) {
  if (!content) return content;
  if (!isDegradedRcaProvenance(provenance)) return content;
  if (content.confidence === 'low') return content;
  return { ...content, confidence: 'low' };
}

/**
 * The single gate between raw model output (or a reporter edit) and persisted
 * state.
 *
 * Returns `{ valid, errors, normalized }`. `normalized` is a COMPLETE content
 * object — every field present, bounded, and type-correct — and is the only value
 * a caller may persist. When `valid` is false the caller must persist NOTHING;
 * `normalized` is still returned so a UI can show the salvageable parts, but it
 * is not a partial success.
 *
 * Unknown keys are REJECTED rather than dropped. A model that invents a field is
 * either hallucinating structure or being steered, and silently discarding it
 * would make the stored analysis a lie about what was proposed.
 */
export function validatePreliminaryRca(input) {
  const errors = [];
  if (input == null || typeof input !== 'object' || Array.isArray(input)) {
    return {
      valid: false,
      errors: [
        { field: 'preliminaryRca', reason: 'type', message: 'The analysis must be an object.' },
      ],
      normalized: emptyPreliminaryRcaContent(),
    };
  }

  const unknown = Object.keys(input).filter((key) => !PRELIMINARY_RCA_CONTENT_FIELDS.includes(key));
  if (unknown.length) {
    for (const key of unknown) {
      pushError(
        errors,
        key,
        PRELIMINARY_RCA_SERVER_FIELDS.includes(key) ? 'server_owned' : 'unknown_key',
        PRELIMINARY_RCA_SERVER_FIELDS.includes(key)
          ? `${key} is set by PRIME and cannot be supplied.`
          : `${key} is not part of the preliminary analysis.`,
      );
    }
  }

  const normalized = emptyPreliminaryRcaContent();

  for (const [field, maxLength] of Object.entries(PRELIMINARY_RCA_TEXT_FIELD_LIMITS)) {
    if (!Object.hasOwn(input, field)) continue;
    normalized[field] = normalizeText(input[field], { field, maxLength, errors });
  }

  for (const [field, bounds] of Object.entries(PRELIMINARY_RCA_LIST_FIELD_LIMITS)) {
    if (!Object.hasOwn(input, field)) continue;
    normalized[field] = normalizeStringList(input[field], {
      field,
      maxItems: bounds.maxItems,
      maxLength: bounds.maxLength,
      errors,
    });
  }

  if (Object.hasOwn(input, 'possibleContributingFactors')) {
    normalized.possibleContributingFactors = normalizeContributingFactors(
      input.possibleContributingFactors,
      errors,
    );
  }

  if (Object.hasOwn(input, 'hypotheses')) {
    normalized.hypotheses = normalizeHypotheses(input.hypotheses, errors);
  }

  if (Object.hasOwn(input, 'suggestedCategory') && input.suggestedCategory != null) {
    if (!isRcaSuggestedCategory(input.suggestedCategory)) {
      pushError(
        errors,
        'suggestedCategory',
        'enum',
        `suggestedCategory must be one of: ${RCA_SUGGESTED_CATEGORIES.join(', ')}.`,
      );
    } else {
      normalized.suggestedCategory = input.suggestedCategory;
    }
  }

  // A supplied confidence is validated for SHAPE (so a model returning
  // "extremely high" is a loud failure, not a silent one) and then discarded:
  // the stored value is always derived.
  if (Object.hasOwn(input, 'confidence') && input.confidence != null) {
    if (!isRcaConfidenceLevel(input.confidence)) {
      pushError(
        errors,
        'confidence',
        'enum',
        `confidence must be one of: ${RCA_CONFIDENCE_LEVELS.join(', ')}.`,
      );
    }
  }
  normalized.confidence = derivePreliminaryRcaConfidence(normalized);

  return { valid: errors.length === 0, errors, normalized };
}

/**
 * The deterministic skeleton: the same shape a model would have produced, with
 * the answers the reporter already gave copied into the fields they belong to and
 * everything else left empty for the reporter to write.
 *
 * This is why an AI outage cannot block submission. It is never labelled as an
 * analysis PRIME produced — `source` is `deterministic_skeleton` and the UI says
 * so.
 */
export function buildDeterministicRcaContent({ messages = [] } = {}) {
  const answers = new Map();
  let lastTopic = null;
  for (const message of messages) {
    const topic = message?.topic ?? message?.payload?.topic ?? null;
    if (message?.actorType === 'assistant' && isRequestProbeTopic(topic)) {
      lastTopic = topic;
      continue;
    }
    if (message?.actorType !== 'user') continue;
    const answerTopic = isRequestProbeTopic(topic) ? topic : lastTopic;
    const text = String(message?.content ?? '').trim();
    if (!answerTopic || !text || answers.has(answerTopic)) continue;
    answers.set(answerTopic, text);
  }

  const content = emptyPreliminaryRcaContent();
  const take = (topic, limit) => String(answers.get(topic) || '').slice(0, limit);
  content.problemStatement = take(
    'coreProblem',
    PRELIMINARY_RCA_TEXT_FIELD_LIMITS.problemStatement,
  );
  content.desiredOutcome = take('desiredOutcome', PRELIMINARY_RCA_TEXT_FIELD_LIMITS.desiredOutcome);
  content.currentWorkaround = take(
    'currentWorkaround',
    PRELIMINARY_RCA_TEXT_FIELD_LIMITS.currentWorkaround,
  );
  content.impact = take('operationalImpact', PRELIMINARY_RCA_TEXT_FIELD_LIMITS.impact);

  const symptomBounds = PRELIMINARY_RCA_LIST_FIELD_LIMITS.observedSymptoms;
  content.observedSymptoms = ['actualBehavior', 'frequency']
    .map((topic) => String(answers.get(topic) || '').slice(0, symptomBounds.maxLength))
    .filter(Boolean);

  const factBounds = PRELIMINARY_RCA_LIST_FIELD_LIMITS.knownFacts;
  content.knownFacts = ['currentProcess', 'expectedBehavior', 'affectedUsers', 'scope']
    .map((topic) => String(answers.get(topic) || '').slice(0, factBounds.maxLength))
    .filter(Boolean);

  // The topics the reporter has NOT answered become the open questions, so the
  // skeleton tells them exactly what is still missing instead of presenting an
  // empty form.
  const missingBounds = PRELIMINARY_RCA_LIST_FIELD_LIMITS.missingInformation;
  content.missingInformation = REQUEST_PROBE_TOPICS.filter((topic) => !answers.has(topic))
    .slice(0, missingBounds.maxItems)
    .map((topic) => REQUEST_FALLBACK_QUESTIONS[topic].slice(0, missingBounds.maxLength));

  content.confidence = derivePreliminaryRcaConfidence(content);
  return content;
}
